/**
 * IndexedDB cache for economic-calendar events.
 *
 * The full multi-year, all-impact catalog is ~34k events (~10 MB serialized) — well over the
 * ~5 MB localStorage quota. IndexedDB (same storage layer the terminal uses for 1m OHLC bars)
 * holds it comfortably. Events live here; the small month-version map stays in localStorage.
 *
 * Isolated in its own database (not the shared VybeTraderDB) so it never contends with the OHLC
 * store's version/upgrade coordination.
 */

import { openDB, type IDBPDatabase } from "idb";
import type { StoredEconomicEvent } from "./types";

const DB_NAME = "VybeEconomicCalendarDB";
const DB_VERSION = 1;
const STORE = "events";
const BLOB_KEY = "all";

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> | null {
  if (typeof indexedDB === "undefined") return null;
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

export async function loadEconomicCalendarEventsIdb(): Promise<StoredEconomicEvent[]> {
  try {
    const db = await getDb();
    if (!db) return [];
    const rec = await db.get(STORE, BLOB_KEY);
    return Array.isArray(rec) ? (rec as StoredEconomicEvent[]) : [];
  } catch (error) {
    console.error("[EconomicCalendar] Failed to load events from IndexedDB:", error);
    return [];
  }
}

export async function saveEconomicCalendarEventsIdb(
  events: StoredEconomicEvent[]
): Promise<void> {
  try {
    const db = await getDb();
    if (!db) return;
    await db.put(STORE, events, BLOB_KEY);
  } catch (error) {
    console.error("[EconomicCalendar] Failed to save events to IndexedDB:", error);
  }
}

export async function clearEconomicCalendarEventsIdb(): Promise<void> {
  try {
    const db = await getDb();
    if (!db) return;
    await db.delete(STORE, BLOB_KEY);
  } catch (error) {
    console.error("[EconomicCalendar] Failed to clear IndexedDB events:", error);
  }
}
